import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { blogPosts, blogHref, categories, locations } from "@/lib/data";
import { ArrowUpRight } from "lucide-react";
import { generateBreadcrumbSchema, generateWebPageSchema } from "@/lib/utils";

const SITE_URL = "https://letslistbusiness.com";

export const Route = createFileRoute("/blog/")({
  head: () => {
    const title = "Local Guides & Business Insights | LetsListBusiness Blog";
    const description = "Discover the best local spots, business tips, and neighborhood guides from our expert contributors.";
    const url = SITE_URL + "/blog/";
    
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: "LetsListBusiness Blog" },
        { property: "og:description", content: "Expert guides to your city." },
      ],
      links: [{ rel: "canonical", href: url }],
      schema: [
        generateWebPageSchema(title, description, url),
        generateBreadcrumbSchema([
          { label: "Home", href: "/" },
          { label: "Blog", href: "/blog/" },
        ], SITE_URL),
      ],
    };
  },
  component: BlogIndex,
});

function BlogIndex() {
  const [feature, ...rest] = blogPosts;
  const featureCat = categories.find((c) => c.slug === feature.category);
  const featureLoc = locations.find((l) => l.slug === feature.location);

  return (
    <>
      <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Blog" }]} />

      {/* Editorial masthead */}
      <section className="border-b border-border">
        <div className="container mx-auto flex flex-wrap items-end justify-between gap-6 px-4 py-10">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">The LetsListBusiness Journal</div>
            <h1 className="mt-3 font-display text-5xl font-bold leading-[1.02] sm:text-6xl">
              Stories from <em className="not-italic text-primary">your neighborhood.</em>
            </h1>
          </div>
          <p className="max-w-sm text-sm text-muted-foreground">
            Best-of city lists, practical guides on choosing service providers,
            and real talk from the people who live there.
          </p>
        </div>
      </section>

      {/* Featured + sidebar */}
      <section className="container mx-auto grid gap-10 px-4 py-12 lg:grid-cols-3">
        <Link {...blogHref(feature.slug)} className="group block lg:col-span-2">
          <div className="overflow-hidden rounded-3xl shadow-card-lg">
            <img src={feature.image} alt={feature.title} className="aspect-16/10 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          </div>
          <div className="mt-6">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              Featured · {featureCat?.name} · {featureLoc?.name}
            </div>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight sm:text-5xl">{feature.title}</h2>
            <p className="mt-4 max-w-2xl text-lg text-muted-foreground">{feature.excerpt}</p>
            <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{feature.author}</span>
              <span>·</span><span>{feature.readTime}</span>
            </div>
          </div>
        </Link>

        <aside>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Latest</div>
          <ul className="mt-4 divide-y divide-border">
            {rest.slice(0, 4).map((p, i) => (
              <li key={p.slug}>
                <Link {...blogHref(p.slug)} className="group flex gap-4 py-4">
                  <span className="font-mono text-xs text-muted-foreground">0{i + 2}</span>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display text-base font-semibold leading-snug group-hover:text-primary">{p.title}</h3>
                    <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{p.excerpt}</p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </aside>
      </section>

      {/* Grid of all posts */}
      <section className="bg-surface py-16">
        <div className="container mx-auto px-4">
          <h2 className="font-display text-2xl font-bold">All articles</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {blogPosts.map((p) => {
              const cat = categories.find((c) => c.slug === p.category);
              const loc = locations.find((l) => l.slug === p.location);
              return (
                <Link key={p.slug} {...blogHref(p.slug)} className="group block overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-lg">
                  <div className="aspect-16/10 overflow-hidden">
                    <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-primary">
                      <span>{cat?.name} · {loc?.name}</span>
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </div>
                    <h3 className="mt-2 font-display text-xl font-semibold leading-tight">{p.title}</h3>
                    <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{p.excerpt}</p>
                    <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                      <span>{p.author}</span>
                      <span>{p.readTime}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
